import React, { useEffect, useMemo } from "react";
import {
  BusinessType,
  BusinessTypeNamingMapping,
  CountryFullName,
  getEmptyCompanyAddress,
  getRegistrationCompanyCountries,
  useKivunovaTranslation,
  usePlatformConfig
} from "@kivunova/kivufrontendcommon";
import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from "@mui/material";
import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  UseFormWatch
} from "react-hook-form";
import { RegistrationData } from "./types/registrationTypes";
import {
  getRegistrationBusinessTypeOptions,
  getRegistrationPlatformConfigState
} from "./utils/registrationBusinessTypeOptions";
import {
  registrationInputSx,
  registrationSelectSx
} from "./registrationFormStyles";
import CompanyVatFields from "../Company/CompanyVatFields";
import CompanyCountryCurrencyAddressSection from "../Company/address/CompanyCountryCurrencyAddressSection";
import { getOrganizationLabelsForBusinessType } from "../../hooks/useOrganizationLabels";

interface CompanyInfoFormProps {
  control: Control<RegistrationData>;
  errors: FieldErrors<RegistrationData>;
  watch: UseFormWatch<RegistrationData>;
  setValue: UseFormSetValue<RegistrationData>;
}

const CompanyInfoForm: React.FC<CompanyInfoFormProps> = ({
  control,
  errors,
  watch,
  setValue
}) => {
  const { t } = useKivunovaTranslation();
  const { businessTypes, featureFlags, loadError } = usePlatformConfig();
  const { registryLoaded, isPlatformConfigLoading } =
    getRegistrationPlatformConfigState(businessTypes, loadError);
  const isBusinessTypeRegistryLoading = isPlatformConfigLoading;
  const registrationCompanyCountries = useMemo(() => {
    if (!registryLoaded) {
      return [];
    }

    return getRegistrationCompanyCountries(featureFlags);
  }, [featureFlags, registryLoaded]);
  const registrationBusinessTypeOptions = useMemo(
    () => getRegistrationBusinessTypeOptions(businessTypes, registryLoaded),
    [businessTypes, registryLoaded]
  );
  const selectedBusinessType = watch("company.businessType");
  const orgLabels = getOrganizationLabelsForBusinessType(
    t,
    selectedBusinessType
  );

  useEffect(() => {
    if (registrationBusinessTypeOptions.length === 0) {
      return;
    }
    const current = watch("company.businessType");
    if (!registrationBusinessTypeOptions.includes(current as BusinessType)) {
      setValue("company.businessType", registrationBusinessTypeOptions[0]);
    }
  }, [registrationBusinessTypeOptions, setValue, watch]);

  useEffect(() => {
    if (registrationCompanyCountries.length === 0) {
      return;
    }

    const selectedCountry = watch("company.address.country");
    if (
      selectedCountry &&
      !registrationCompanyCountries.includes(selectedCountry)
    ) {
      setValue("company.address", getEmptyCompanyAddress(CountryFullName.RW));
      setValue("company.currencyCode", "RWF");
    }
  }, [registrationCompanyCountries, setValue, watch]);

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}
      id="company-info-form"
    >
      <Controller
        name="company.name"
        control={control}
        render={({ field }) => (
          <TextField
            required
            {...field}
            label={orgLabels.nameLabel}
            fullWidth
            error={!!errors?.company?.name}
            helperText={errors?.company?.name?.message}
            id="registration-company-name-input"
            sx={registrationInputSx}
          />
        )}
      />

      <Controller
        name="company.businessType"
        control={control}
        render={({ field }) => {
          return (
            <FormControl style={{ width: "100%" }}>
              <InputLabel shrink id="business-type-select-label">
                {t("business-type-label-text", "Business Type")}
              </InputLabel>
              <Select
                {...field}
                labelId="business-type-select-label"
                id="business-type-select"
                required
                label={t("business-type", "Business Type")}
                fullWidth
                disabled={isBusinessTypeRegistryLoading}
                displayEmpty={isBusinessTypeRegistryLoading}
                error={!!errors?.company?.businessType}
                value={field.value}
                renderValue={
                  isBusinessTypeRegistryLoading
                    ? () =>
                        t(
                          "registration-business-type-loading",
                          "Loading business types…"
                        )
                    : undefined
                }
                inputProps={{ "data-testid": "registration-businessType" }}
                sx={registrationSelectSx}
              >
                {registrationBusinessTypeOptions.map((type) => (
                  <MenuItem
                    key={type}
                    value={type}
                    id={`registration-businessType-${type}`}
                  >
                    {t(
                      `business-type-${BusinessTypeNamingMapping[type]}`,
                      BusinessTypeNamingMapping[type]
                    )}
                  </MenuItem>
                ))}
              </Select>
              {(errors?.company?.businessType?.message ||
                (loadError && registryLoaded)) && (
                <FormHelperText
                  error={
                    !!errors?.company?.businessType ||
                    (loadError && registryLoaded)
                  }
                >
                  {errors?.company?.businessType?.message ??
                    t(
                      "registration-business-type-load-error",
                      "Could not load business types. Only Trade & Retail is available."
                    )}
                </FormHelperText>
              )}
            </FormControl>
          );
        }}
      />

      <Controller
        name="company.registrationNumber"
        control={control}
        render={({ field }) => (
          <TextField
            required
            {...field}
            onChange={(e) => {
              const cleaned = e.target.value.replace(/[^A-Za-z0-9]/g, "");
              field.onChange(cleaned);
            }}
            label={t("registration-number", "Registration Number")}
            id="registration-number-input"
            fullWidth
            error={!!errors?.company?.registrationNumber}
            helperText={errors?.company?.registrationNumber?.message}
            inputProps={{
              "data-testid": "registration-registrationNumber",
              maxLength: 64
            }}
            sx={registrationInputSx}
          />
        )}
      />

      <CompanyCountryCurrencyAddressSection
        control={control as never}
        errors={errors}
        watch={watch as never}
        setValue={setValue as never}
        addressFieldPrefix="company.address"
        currencyFieldName="company.currencyCode"
        countryEditable
        availableCountries={registrationCompanyCountries}
        countrySelectLoading={isPlatformConfigLoading}
        idPrefix="registration-"
        selectSx={registrationSelectSx}
        inputSx={registrationInputSx}
      />

      <CompanyVatFields
        control={control}
        vatPercentageName="company.vatPercentage"
        vatCalculationModeName="company.vatCalculationMode"
        selectSx={registrationSelectSx}
        vatPercentageTestId="registration-vatPercentage"
        vatCalculationModeTestId="registration-vatCalculationMode"
      />

      <Controller
        name="company.description"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label={t("description", "Description")}
            multiline
            rows={3}
            fullWidth
            id="registration-description-text-area"
            inputProps={{ "data-testid": "registration-description" }}
            sx={registrationInputSx}
          />
        )}
      />
    </Box>
  );
};

export default CompanyInfoForm;
